import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { PageHeader } from '../components/navigation';
import { ModernCard } from '../components/ui';
import { 
  Users, 
  MessageSquare, 
  Heart,
  Share2,
  ChevronRight,
  Star,
  Clock,
  MapPin,
  Calendar,
  Globe,
  Award,
  TrendingUp,
  MessageCircle,
  Plus,
  Search,
  Filter
} from 'lucide-react';

const CommunityPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'stories' | 'qa' | 'cultural'>('stories');

  // SEO optimization
  useSEO({
    title: 'Community - MyNewStart',
    description: 'Connect with fellow immigrants, share stories, and find support in your American journey.',
    keywords: ['community', 'stories', 'support', 'immigrants', 'cultural'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Community', url: '/community' }
    ]
  });

  const tabs = [
    { id: 'stories', name: 'Stories', icon: MessageSquare },
    { id: 'qa', name: 'Q&A', icon: MessageCircle },
    { id: 'cultural', name: 'Cultural', icon: Globe }
  ];

  const stories = [
    {
      id: 1,
      title: 'From Engineer to Entrepreneur: My 5-Year Journey',
      author: 'Maria Santos',
      authorCountry: 'Mexico',
      timeAgo: '2 hours ago',
      likes: 24,
      comments: 8,
      excerpt: 'When I first arrived in the US, I never imagined I\'d start my own tech company. Here\'s how I turned my engineering background into a successful business...',
      tags: ['Career', 'Entrepreneurship', 'Success'],
      image: '/api/placeholder/300/200'
    },
    {
      id: 2,
      title: 'Building Credit from Zero: What I Wish I Knew',
      author: 'Ahmed Hassan',
      authorCountry: 'Egypt',
      timeAgo: '5 hours ago',
      likes: 18,
      comments: 12,
      excerpt: 'Starting with no credit history was challenging, but these strategies helped me build a solid credit score in just 18 months...',
      tags: ['Finance', 'Credit', 'Tips'],
      image: '/api/placeholder/300/200'
    },
    {
      id: 3,
      title: 'Finding My Community in a New City',
      author: 'Priya Patel',
      authorCountry: 'India',
      timeAgo: '1 day ago',
      likes: 31,
      comments: 15,
      excerpt: 'Moving to a new city alone was scary, but I found amazing people who became my chosen family. Here\'s how I built my support network...',
      tags: ['Community', 'Friendship', 'Support'],
      image: '/api/placeholder/300/200'
    }
  ];

  const qaItems = [
    {
      id: 1,
      question: 'How long does it take to get a Social Security Number?',
      author: 'New User',
      timeAgo: '3 hours ago',
      answers: 4,
      category: 'Documentation',
      isAnswered: true
    },
    {
      id: 2,
      question: 'Best banks for new immigrants with no credit history?',
      author: 'Finance Seeker',
      timeAgo: '6 hours ago',
      answers: 7,
      category: 'Banking',
      isAnswered: true
    },
    {
      id: 3,
      question: 'How to find affordable housing in San Francisco?',
      author: 'Housing Hunter',
      timeAgo: '1 day ago',
      answers: 2,
      category: 'Housing',
      isAnswered: false
    }
  ];

  const culturalSpotlight = [
    {
      id: 1,
      title: 'Celebrating Diwali in America',
      description: 'How to maintain cultural traditions while embracing new experiences',
      author: 'Cultural Committee',
      image: '/api/placeholder/300/200',
      participants: 45
    },
    {
      id: 2,
      title: 'Mexican Independence Day Celebration',
      description: 'Join our community celebration in downtown',
      author: 'Latino Community Center',
      image: '/api/placeholder/300/200',
      participants: 120
    }
  ];

  const communityStats = {
    totalMembers: 1250,
    activeToday: 89,
    storiesShared: 340,
    questionsAnswered: 1250
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <PageHeader
            title="Community"
            description="Connect with fellow immigrants and share your journey"
            icon={<Users className="h-6 w-6 text-purple-600" />}
          />
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search stories, questions, and topics..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1">
              <Filter className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pb-4">
          <div className="flex space-x-1 bg-gray-100 rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center py-2 px-3 rounded-lg transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Community Stats */}
        <div className="grid grid-cols-2 gap-4">
          <ModernCard className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600 mb-1">{communityStats.totalMembers.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Total Members</div>
          </ModernCard>
          <ModernCard className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600 mb-1">{communityStats.activeToday}</div>
            <div className="text-sm text-gray-600">Active Today</div>
          </ModernCard>
        </div>

        {activeTab === 'stories' && (
          <div className="space-y-4">
            {/* Share Story Button */}
            <button className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors flex items-center justify-center">
              <Plus className="w-5 h-5 mr-2" />
              Share Your Story
            </button>

            {/* Stories List */}
            <div className="space-y-4">
              {stories.map((story) => (
                <ModernCard key={story.id} className="p-4">
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-purple-600">
                        {story.author.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <h4 className="font-semibold text-gray-900 mr-2">{story.author}</h4>
                        <span className="text-sm text-gray-500">from {story.authorCountry}</span>
                      </div>
                      <p className="text-sm text-gray-500">{story.timeAgo}</p>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-gray-900 mb-2">{story.title}</h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-3">{story.excerpt}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {story.tags.map((tag, index) => (
                      <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button className="flex items-center text-gray-500 hover:text-red-500 transition-colors">
                        <Heart className="w-4 h-4 mr-1" />
                        <span className="text-sm">{story.likes}</span>
                      </button>
                      <button className="flex items-center text-gray-500 hover:text-blue-500 transition-colors">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        <span className="text-sm">{story.comments}</span>
                      </button>
                      <button className="flex items-center text-gray-500 hover:text-green-500 transition-colors">
                        <Share2 className="w-4 h-4 mr-1" />
                        <span className="text-sm">Share</span>
                      </button>
                    </div>
                    <button className="text-purple-600 text-sm font-medium hover:text-purple-700">
                      Read More
                    </button>
                  </div>
                </ModernCard>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'qa' && (
          <div className="space-y-4">
            {/* Ask Question Button */}
            <button className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center">
              <Plus className="w-5 h-5 mr-2" />
              Ask a Question
            </button>

            {/* Q&A List */}
            <div className="space-y-4">
              {qaItems.map((item) => (
                <ModernCard key={item.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h4 className="font-semibold text-gray-900 mr-2">{item.question}</h4>
                        {item.isAnswered && (
                          <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
                            Answered
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-sm text-gray-500 mb-2">
                        <span className="mr-4">by {item.author}</span>
                        <span className="mr-4">{item.timeAgo}</span>
                        <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs">
                          {item.category}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        <span>{item.answers} answers</span>
                      </div>
                    </div>
                    <button className="p-2 text-gray-400 hover:text-gray-600">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </ModernCard>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'cultural' && (
          <div className="space-y-4">
            {/* Cultural Spotlight */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cultural Spotlight</h3>
              <div className="space-y-4">
                {culturalSpotlight.map((item) => (
                  <ModernCard key={item.id} className="p-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0"></div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                        <p className="text-gray-600 text-sm mb-2">{item.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">by {item.author}</span>
                          <div className="flex items-center text-sm text-gray-500">
                            <Users className="w-4 h-4 mr-1" />
                            <span>{item.participants} participants</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ModernCard>
                ))}
              </div>
            </div>

            {/* Cultural Events */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Events</h3>
              <div className="space-y-3">
                <ModernCard className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                        <Calendar className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">International Food Festival</h4>
                        <p className="text-sm text-gray-500">Dec 15, 2024 • Central Park</p>
                      </div>
                    </div>
                    <button className="text-blue-600 text-sm font-medium">Join</button>
                  </div>
                </ModernCard>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Floating Action Button for Mini Angel */}
      <button 
        onClick={() => navigate('/chat')}
        className="fixed bottom-28 right-4 md:bottom-8 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-40"
      >
        <MessageSquare className="w-6 h-6" />
      </button>
    </div>
  );
};

export default CommunityPage;
